import { ITwoWayConversationEntity } from '@gr/shared/models';
import moment from 'moment';
import { toPhoneNumberFormat } from '../../providers/utility.provider';
import { NewButton } from '../shared/Buttons/NewButton';
import { ButtonVariantEnum } from '../shared/Buttons/types';

interface IMessagesProps {
  activeMessage: ITwoWayConversationEntity | undefined;
  messages: ITwoWayConversationEntity[];
  setActiveMessage: (message) => void;
  setActiveResponse: (response) => void;
  setSentConvos: (convos) => void;
  loading: boolean;
  searchText?: string;
  setActiveMessageLoading: (loading) => void;
  replyLoading: boolean;
  hasNextPage: boolean | undefined;
  hasActiveFilter: boolean;
  noMoreConversations: boolean;
  onClearFilters: () => void;
  onBootstrap: () => void;
  bootstrapLoading: boolean;
}

export const Messages = ({
  setActiveMessage,
  setActiveResponse,
  setSentConvos,
  messages,
  activeMessage,
  loading,
  searchText,
  setActiveMessageLoading,
  replyLoading,
  hasNextPage,
  hasActiveFilter,
  noMoreConversations,
  onClearFilters,
  onBootstrap,
  bootstrapLoading,
}: IMessagesProps) => {
  if (loading && messages.length === 0) {
    return (
      <div className="max-h-full overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <div key={i}>
            <div className="con-message">
              <div className="flex flex-col w-full gap-2">
                <div className="w-2/5 h-4 bg-gray-200 rounded animate-pulse" />
                <div className="w-1/3 h-3 bg-gray-200 rounded animate-pulse" />
                <div className="w-4/5 h-3 mt-2 bg-gray-200 rounded animate-pulse" />
                <div className="w-3/5 h-3 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="w-10 h-3 ml-4 bg-gray-200 rounded animate-pulse shrink-0" />
            </div>
            <hr />
          </div>
        ))}
      </div>
    );
  }

  if (messages.length > 0) {
    return (
      <div className="max-h-full overflow-auto">
        {messages
          .filter((m) => m.clientNumber.toString().includes(searchText ?? '') && !m.optoutId)
          .map((message) => (
            <div key={message.id}>
              <div
                className={`${activeMessage?.id === message?.id && 'con-message-active'} con-message`}
                style={replyLoading ? { cursor: 'default' } : {}}
                onClick={() => {
                  if (!replyLoading) {
                    setActiveMessageLoading(true);
                    if (activeMessage?.id !== message.id) {
                      setActiveResponse(undefined);
                    }
                    setActiveMessage(message);
                    // Remove sent convos so they don't show in the new active message
                    setSentConvos([]);
                  }
                }}
              >
                <div className="flex flex-col text-ellipsis">
                  <div className="whitespace-nowrap body-text-bold">{message.clientName}</div>
                  <div className="whitespace-nowrap body-text-small text-medium-gray">
                    {toPhoneNumberFormat(message.clientNumber.toString())}
                  </div>
                  <div className="mt-2 line-clamp-2 body-text-small text-dark-gray">{message.latestReply}</div>
                </div>
                <div className="whitespace-nowrap text-medium-gray">{moment(message.latestReplyAt).format('LT')}</div>
              </div>
              <hr />
            </div>
          ))}
        {noMoreConversations && (
          <div className="px-12 py-6 text-center body-text text-black font-semibold">
            No more conversations for the selected filter
          </div>
        )}
      </div>
    );
  }

  if (hasActiveFilter) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="px-12 text-center body-text text-dark-gray">
          No conversations match the selected filter.
        </div>
        <NewButton
          variant={ButtonVariantEnum.DELETE}
          onClick={onClearFilters}
          className="mt-6"
        >
          CLEAR FILTERS
        </NewButton>
      </div>
    );
  }

  if (hasNextPage === false) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="px-12 text-center body-text text-dark-gray">
          Load conversations to get a targeted chunk assigned to you.
        </div>
        <NewButton
          disabled={bootstrapLoading}
          isLoading={bootstrapLoading}
          onClick={onBootstrap}
          className="mx-auto mt-10 grow-0"
        >
          LOAD CONVERSATIONS
        </NewButton>
      </div>
    );
  }

  return null;
};
